import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ColDef, ColType } from '../../interfaces/colDef';
import { SYNTHETIC_FILTER_KEYS } from '../../dual-axis/dual-axis.component';

// Scalar column types that sort meaningfully in a card-view "Sort by" picker.
// Excludes list/composite types (LISTADDER, AREA, CUSTOM, RANGE, SLIDER, …) that
// have no sensible single-key order.
const SORTABLE_TYPES: ColType[] = [ColType.STRING, ColType.INT, ColType.DATE, ColType.CURRENCY, ColType.SELECT];

/**
 * Shared "Sort by" list for card-view sorting. Used by both the desktop Table
 * Options panel and the mobile Market filter sheet so the sort UI + curated-column
 * rule live in one place. Lists the sortable columns (see sortableColumns for the rule),
 * highlights the active one with a direction glyph, and emits (pick) — the host applies
 * it (dual-axis setSort, which owns direction + the server re-fetch). Renders nothing
 * when there are no sortable columns.
 */
@Component({
  selector: 'bst-sort-options',
  template: `
    @if (sortableColumns.length) {
      <div class="sort-options">
        <div class="sort-label">{{ 'sortby' | translate }}</div>
        <div class="sort-list">
          @for (c of sortableColumns; track c.name) {
            <button type="button" class="sort-row" [class.sort-row--active]="isActive(c)"
                    (click)="pick.emit(c)">
              <span>{{ c.title | translate }}</span>
              @if (isActive(c)) { <nb-icon [icon]="glyph()"></nb-icon> }
            </button>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['./sort-options.component.scss'],
  standalone: false,
})
export class SortOptionsComponent {
  @Input() colDefs: ColDef[] = [];
  @Input() currentSort: { col: string, dir: number } | null = null;
  @Output() pick = new EventEmitter<ColDef>();

  /**
   * Sortable columns for the picker. Follows the table-header rule (`sort !== false`),
   * then includes a column if EITHER it explicitly opts in (`sort` truthy — respects an
   * author who marked e.g. a PHONE/LOCATION column sortable) OR it's a scalar
   * SORTABLE_TYPE relying on the default (so Projects/Clients name + dates appear while
   * LISTADDER/AREA junk is dropped). Non-synthetic, deduped by name (sticky + standard
   * sources can overlap).
   */
  public get sortableColumns(): ColDef[] {
    const seen = new Set<string>();
    return (this.colDefs || []).filter(c =>
      c.sort !== false
      && (!!c.sort || SORTABLE_TYPES.includes(c.type))
      && !SYNTHETIC_FILTER_KEYS.includes(c.name)
      && !seen.has(c.name) && seen.add(c.name) !== undefined);
  }

  /** True if this column is the active primary sort (matches sort-alias OR name). */
  public isActive(col: ColDef): boolean {
    const cur = this.currentSort?.col;
    if (cur === null || cur === undefined) { return false; }
    return cur === col.sort || cur === col.name;
  }

  /** arrow-down = desc (dir 2), arrow-up = asc (dir 1). */
  public glyph(): string {
    return this.currentSort?.dir === 2 ? 'arrow-down-outline' : 'arrow-up-outline';
  }
}
